import React, { useCallback, useMemo } from 'react';
import type { BillingCodeSourceMap } from '@features/encounters';
import { Add as AddIcon, AutoAwesome, InfoOutlined } from '@mui/icons-material';
import { Autocomplete, Box, Button, Chip, Stack, TextField, Tooltip, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';

export type ICD10Category =
  | 'Common'
  | 'Depression'
  | 'Anxiety'
  | 'Trauma & Stress'
  | 'Bipolar'
  | 'Psychotic Disorders'
  | 'ADHD & Neurodevelopmental'
  | 'Sleep'
  | 'OCD & Related'
  | 'Eating Disorders'
  | 'Personality Disorders'
  | 'Substance Use - Alcohol'
  | 'Substance Use - Opioids'
  | 'Substance Use - Cannabis'
  | 'Substance Use - Stimulants'
  | 'Substance Use - Sedatives'
  | 'Substance Use - Nicotine'
  | 'Substance Use - Other'
  | 'Dissociative'
  | 'Somatic'
  | 'Cognitive'
  | 'Behavioral & Other Mental'
  | 'Symptoms (R-codes)'
  | 'Z-Codes (Psychosocial/Context)'
  | 'Z-Codes (Medication/Adherence)'
  | 'Z-Codes (Family/Relationship)'
  | 'Z-Codes (Abuse/Trauma History)'
  | 'Z-Codes (Work/School)'
  | 'Z-Codes (Housing/Econ)'
  | 'Z-Codes (Legal/Forensic)'
  | 'Z-Codes (Other)';

export type ICD10CodeOption = {
  icd10: string;
  description: string;
  category: ICD10Category;
  /** Optional extra search strings for autocomplete */
  synonyms?: string[];
  /** When true, this is more of a "status/modifier/context" than a core psychiatric dx */
  isModifierLike?: boolean;
  /** When true, this is a synthetic "Add custom code" option appended by filterOptions */
  __custom?: boolean;
};

export const ICD10_CODES: ICD10CodeOption[] = [
  // ICD-10-CM behavioral-health code list. Codes & official descriptions are
  // US Government public domain, code set FY2026 (effective Oct 1, 2025).
  // Source of truth: NLM Clinical Tables ICD-10-CM API + CDC/NCHS FY2026.
  // To add a code, verify it is a BILLABLE leaf (not a header) at
  // https://clinicaltables.nlm.nih.gov/api/icd10cm/v3/search?sf=code,name&terms=<CODE>
  // -----------------------
  // Common
  // -----------------------
  {
    icd10: 'F41.1',
    description: 'Generalized anxiety disorder',
    category: 'Common',
    synonyms: ['GAD', 'anxiety'],
  },
  {
    icd10: 'F33.1',
    description: 'Major depressive disorder, recurrent, moderate',
    category: 'Common',
    synonyms: ['MDD recurrent moderate', 'depression'],
  },
  {
    icd10: 'F33.0',
    description: 'Major depressive disorder, recurrent, mild',
    category: 'Common',
    synonyms: ['MDD recurrent mild', 'depression'],
  },
  {
    icd10: 'F33.2',
    description: 'Major depressive disorder, recurrent, severe w/o psychotic features',
    category: 'Common',
    synonyms: ['MDD recurrent severe'],
  },
  {
    icd10: 'F32.1',
    description: 'Major depressive disorder, single episode, moderate',
    category: 'Common',
    synonyms: ['MDD single moderate'],
  },
  {
    icd10: 'F32.0',
    description: 'Major depressive disorder, single episode, mild',
    category: 'Common',
    synonyms: ['MDD single mild'],
  },
  {
    icd10: 'F43.10',
    description: 'Post-traumatic stress disorder, unspecified',
    category: 'Common',
    synonyms: ['PTSD'],
  },
  {
    icd10: 'F90.2',
    description: 'Attention-deficit hyperactivity disorder, combined type',
    category: 'Common',
    synonyms: ['ADHD combined'],
  },
  {
    icd10: 'F90.0',
    description: 'Attention-deficit hyperactivity disorder, predominantly inattentive type',
    category: 'Common',
    synonyms: ['ADHD inattentive', 'ADD'],
  },
  {
    icd10: 'F50.819',
    description: 'Binge eating disorder, unspecified',
    category: 'Common',
    synonyms: ['BED', 'binge eating'],
  },
  {
    icd10: 'F51.01',
    description: 'Primary insomnia',
    category: 'Common',
    synonyms: ['insomnia'],
  },
  {
    icd10: 'F51.05',
    description: 'Insomnia due to other mental disorder',
    category: 'Common',
    synonyms: ['insomnia', 'sleep'],
  },
  {
    icd10: 'F42.2',
    description: 'Mixed obsessional thoughts and acts',
    category: 'Common',
    synonyms: ['OCD'],
  },
  {
    icd10: 'F60.3',
    description: 'Borderline personality disorder',
    category: 'Common',
    synonyms: ['BPD'],
  },
  {
    icd10: 'F10.20',
    description: 'Alcohol dependence, uncomplicated',
    category: 'Common',
    synonyms: ['AUD', 'alcohol use disorder'],
  },
  {
    icd10: 'F11.20',
    description: 'Opioid dependence, uncomplicated',
    category: 'Common',
    synonyms: ['OUD', 'opioid use disorder'],
  },
  {
    icd10: 'F17.200',
    description: 'Nicotine dependence, unspecified, uncomplicated',
    category: 'Common',
    synonyms: ['tobacco use', 'nicotine'],
  },
  {
    icd10: 'F12.20',
    description: 'Cannabis dependence, uncomplicated',
    category: 'Common',
    synonyms: ['cannabis use disorder'],
  },
  // -----------------------
  // Depression
  // -----------------------
  {
    icd10: 'F32.9',
    description: 'Major depressive disorder, single episode, unspecified',
    category: 'Depression',
  },
  {
    icd10: 'F32.2',
    description: 'Major depressive disorder, single episode, severe w/o psychotic features',
    category: 'Depression',
  },
  {
    icd10: 'F32.3',
    description: 'Major depressive disorder, single episode, severe with psychotic features',
    category: 'Depression',
  },
  {
    icd10: 'F33.9',
    description: 'Major depressive disorder, recurrent, unspecified',
    category: 'Depression',
  },
  {
    icd10: 'F33.3',
    description: 'Major depressive disorder, recurrent, severe with psychotic features',
    category: 'Depression',
  },
  {
    icd10: 'F33.41',
    description: 'Major depressive disorder, recurrent, in partial remission',
    category: 'Depression',
    synonyms: ['partial remission'],
    isModifierLike: true,
  },
  {
    icd10: 'F33.42',
    description: 'Major depressive disorder, recurrent, in full remission',
    category: 'Depression',
    synonyms: ['full remission'],
    isModifierLike: true,
  },
  {
    icd10: 'F34.1',
    description: 'Dysthymia (persistent depressive disorder)',
    category: 'Depression',
    synonyms: ['PDD', 'dysthymic'],
  },
  {
    icd10: 'F34.0',
    description: 'Cyclothymic disorder',
    category: 'Depression',
    synonyms: ['cyclothymia'],
  },
  {
    icd10: 'F53.0',
    description: 'Postpartum depression',
    category: 'Depression',
    synonyms: ['postpartum', 'peripartum'],
  },
  {
    icd10: 'F39',
    description: 'Unspecified mood [affective] disorder',
    category: 'Depression',
  },
  {
    icd10: 'F34.81',
    description: 'Disruptive mood dysregulation disorder',
    category: 'Depression',
    synonyms: ['DMDD', 'disruptive mood dysregulation'],
  },
  {
    icd10: 'F32.81',
    description: 'Premenstrual dysphoric disorder',
    category: 'Depression',
    synonyms: ['PMDD', 'premenstrual dysphoric'],
  },
  {
    icd10: 'F32.4',
    description: 'Major depressive disorder, single episode, in partial remission',
    category: 'Depression',
    synonyms: ['remission'],
  },
  {
    icd10: 'F32.5',
    description: 'Major depressive disorder, single episode, in full remission',
    category: 'Depression',
    synonyms: ['remission'],
  },
  {
    icd10: 'F34.89',
    description: 'Other specified persistent mood disorders',
    category: 'Depression',
  },
  {
    icd10: 'F32.89',
    description: 'Other specified depressive episodes',
    category: 'Depression',
  },
  {
    icd10: 'F32.A',
    description: 'Depression, unspecified',
    category: 'Depression',
    synonyms: ['depression'],
  },
  {
    icd10: 'F34.9',
    description: 'Persistent mood [affective] disorder, unspecified',
    category: 'Depression',
  },
  // -----------------------
  // Anxiety
  // -----------------------
  {
    icd10: 'F41.0',
    description: 'Panic disorder [episodic paroxysmal anxiety]',
    category: 'Anxiety',
    synonyms: ['panic attacks'],
  },
  {
    icd10: 'F41.9',
    description: 'Anxiety disorder, unspecified',
    category: 'Anxiety',
  },
  {
    icd10: 'F40.10',
    description: 'Social phobia, unspecified',
    category: 'Anxiety',
    synonyms: ['social anxiety'],
  },
  {
    icd10: 'F40.00',
    description: 'Agoraphobia, unspecified',
    category: 'Anxiety',
  },
  {
    icd10: 'F40.01',
    description: 'Agoraphobia with panic disorder',
    category: 'Anxiety',
  },
  {
    icd10: 'F40.11',
    description: 'Social phobia, generalized',
    category: 'Anxiety',
  },
  {
    icd10: 'F40.231',
    description: 'Fear of flying',
    category: 'Anxiety',
    synonyms: ['phobia', 'aviophobia'],
  },
  {
    icd10: 'F40.240',
    description: 'Claustrophobia',
    category: 'Anxiety',
    synonyms: ['phobia'],
  },
  {
    icd10: 'F40.218',
    description: 'Other specified phobia',
    category: 'Anxiety',
    synonyms: ['phobia'],
  },
  {
    icd10: 'F93.0',
    description: 'Separation anxiety disorder of childhood',
    category: 'Anxiety',
    synonyms: ['separation anxiety'],
  },
  {
    icd10: 'F41.3',
    description: 'Other mixed anxiety disorders',
    category: 'Anxiety',
    synonyms: ['mixed anxiety'],
  },
  {
    icd10: 'F41.8',
    description: 'Other specified anxiety disorders',
    category: 'Anxiety',
  },
  {
    icd10: 'F40.8',
    description: 'Other phobic anxiety disorders',
    category: 'Anxiety',
  },
  {
    icd10: 'F40.9',
    description: 'Phobic anxiety disorder, unspecified',
    category: 'Anxiety',
  },
  {
    icd10: 'F40.02',
    description: 'Agoraphobia without panic disorder',
    category: 'Anxiety',
    synonyms: ['agoraphobia'],
  },
  // -----------------------
  // Trauma & Stress
  // -----------------------
  {
    icd10: 'F43.11',
    description: 'Post-traumatic stress disorder, acute',
    category: 'Trauma & Stress',
    synonyms: ['PTSD acute'],
    isModifierLike: true,
  },
  {
    icd10: 'F43.12',
    description: 'Post-traumatic stress disorder, chronic',
    category: 'Trauma & Stress',
    synonyms: ['PTSD chronic'],
    isModifierLike: true,
  },
  {
    icd10: 'F43.0',
    description: 'Acute stress reaction',
    category: 'Trauma & Stress',
    synonyms: ['acute stress'],
  },
  {
    icd10: 'F43.21',
    description: 'Adjustment disorder with depressed mood',
    category: 'Trauma & Stress',
    synonyms: ['adjustment disorder'],
  },
  {
    icd10: 'F43.22',
    description: 'Adjustment disorder with anxiety',
    category: 'Trauma & Stress',
    synonyms: ['adjustment disorder'],
  },
  {
    icd10: 'F43.23',
    description: 'Adjustment disorder with mixed anxiety and depressed mood',
    category: 'Trauma & Stress',
    synonyms: ['adjustment disorder'],
  },
  {
    icd10: 'F43.24',
    description: 'Adjustment disorder with disturbance of conduct',
    category: 'Trauma & Stress',
  },
  {
    icd10: 'F43.25',
    description: 'Adjustment disorder with mixed disturbance of emotions and conduct',
    category: 'Trauma & Stress',
  },
  {
    icd10: 'F43.20',
    description: 'Adjustment disorder, unspecified',
    category: 'Trauma & Stress',
  },
  {
    icd10: 'F43.8',
    description: 'Other reactions to severe stress',
    category: 'Trauma & Stress',
  },
  {
    icd10: 'F43.9',
    description: 'Reaction to severe stress, unspecified',
    category: 'Trauma & Stress',
  },
  {
    icd10: 'F94.1',
    description: 'Reactive attachment disorder of childhood',
    category: 'Trauma & Stress',
    synonyms: ['reactive attachment', 'RAD'],
  },
  {
    icd10: 'F94.2',
    description: 'Disinhibited attachment disorder of childhood',
    category: 'Trauma & Stress',
    synonyms: ['disinhibited social engagement', 'DSED'],
  },
  // -----------------------
  // Bipolar
  // -----------------------
  {
    icd10: 'F31.9',
    description: 'Bipolar disorder, unspecified',
    category: 'Bipolar',
  },
  {
    icd10: 'F31.0',
    description: 'Bipolar disorder, current episode hypomanic',
    category: 'Bipolar',
  },
  {
    icd10: 'F31.10',
    description: 'Bipolar disorder, current episode manic without psychotic features, unspecified',
    category: 'Bipolar',
  },
  {
    icd10: 'F31.2',
    description: 'Bipolar disorder, current episode manic with psychotic features',
    category: 'Bipolar',
  },
  {
    icd10: 'F31.30',
    description: 'Bipolar disorder, current episode depressed, mild/moderate, unspecified',
    category: 'Bipolar',
  },
  {
    icd10: 'F31.31',
    description: 'Bipolar disorder, current episode depressed, mild',
    category: 'Bipolar',
  },
  {
    icd10: 'F31.32',
    description: 'Bipolar disorder, current episode depressed, moderate',
    category: 'Bipolar',
  },
  {
    icd10: 'F31.4',
    description: 'Bipolar disorder, current episode depressed, severe w/o psychotic features',
    category: 'Bipolar',
  },
  {
    icd10: 'F31.5',
    description: 'Bipolar disorder, current episode depressed, severe with psychotic features',
    category: 'Bipolar',
  },
  {
    icd10: 'F31.70',
    description: 'Bipolar disorder, currently in remission, most recent episode unspecified',
    category: 'Bipolar',
    synonyms: ['remission'],
    isModifierLike: true,
  },
  {
    icd10: 'F31.81',
    description: 'Bipolar II disorder',
    category: 'Bipolar',
    synonyms: ['bipolar 2'],
  },
  {
    icd10: 'F31.61',
    description: 'Bipolar disorder, current episode mixed, mild',
    category: 'Bipolar',
  },
  {
    icd10: 'F31.62',
    description: 'Bipolar disorder, current episode mixed, moderate',
    category: 'Bipolar',
  },
  {
    icd10: 'F31.71',
    description: 'Bipolar disorder, in partial remission, most recent episode hypomanic',
    category: 'Bipolar',
  },
  {
    icd10: 'F31.72',
    description: 'Bipolar disorder, in full remission, most recent episode hypomanic',
    category: 'Bipolar',
  },
  {
    icd10: 'F31.73',
    description: 'Bipolar disorder, in partial remission, most recent episode manic',
    category: 'Bipolar',
  },
  {
    icd10: 'F31.74',
    description: 'Bipolar disorder, in full remission, most recent episode manic',
    category: 'Bipolar',
  },
  {
    icd10: 'F31.75',
    description: 'Bipolar disorder, in partial remission, most recent episode depressed',
    category: 'Bipolar',
  },
  {
    icd10: 'F31.76',
    description: 'Bipolar disorder, in full remission, most recent episode depressed',
    category: 'Bipolar',
  },
  {
    icd10: 'F31.89',
    description: 'Other bipolar disorder',
    category: 'Bipolar',
  },
  // -----------------------
  // Psychotic Disorders
  // -----------------------
  {
    icd10: 'F20.9',
    description: 'Schizophrenia, unspecified',
    category: 'Psychotic Disorders',
  },
  {
    icd10: 'F20.0',
    description: 'Paranoid schizophrenia',
    category: 'Psychotic Disorders',
  },
  {
    icd10: 'F25.0',
    description: 'Schizoaffective disorder, bipolar type',
    category: 'Psychotic Disorders',
  },
  {
    icd10: 'F25.1',
    description: 'Schizoaffective disorder, depressive type',
    category: 'Psychotic Disorders',
  },
  {
    icd10: 'F29',
    description: 'Unspecified psychosis not due to a substance or known physiological condition',
    category: 'Psychotic Disorders',
    synonyms: ['psychosis NOS'],
  },
  {
    icd10: 'F22',
    description: 'Delusional disorders',
    category: 'Psychotic Disorders',
  },
  {
    icd10: 'F25.9',
    description: 'Schizoaffective disorder, unspecified',
    category: 'Psychotic Disorders',
    synonyms: ['schizoaffective'],
  },
  {
    icd10: 'F21',
    description: 'Schizotypal disorder',
    category: 'Psychotic Disorders',
    synonyms: ['schizotypal'],
  },
  {
    icd10: 'F25.8',
    description: 'Other schizoaffective disorders',
    category: 'Psychotic Disorders',
  },
  // -----------------------
  // ADHD & Neurodevelopmental
  // -----------------------
  {
    icd10: 'F90.1',
    description: 'ADHD, predominantly hyperactive type',
    category: 'ADHD & Neurodevelopmental',
    synonyms: ['ADHD hyperactive'],
  },
  {
    icd10: 'F98.8',
    description: 'Other specified behavioral and emotional disorders with onset usually in childhood',
    category: 'ADHD & Neurodevelopmental',
  },
  {
    icd10: 'F84.0',
    description: 'Autistic disorder (Autism spectrum disorder)',
    category: 'ADHD & Neurodevelopmental',
    synonyms: ['ASD', 'autism'],
  },
  {
    icd10: 'F84.5',
    description: "Asperger's syndrome",
    category: 'ADHD & Neurodevelopmental',
    synonyms: ['aspergers'],
  },
  {
    icd10: 'F81.9',
    description: 'Developmental disorder of scholastic skills, unspecified',
    category: 'ADHD & Neurodevelopmental',
    synonyms: ['learning disorder'],
  },
  {
    icd10: 'F90.8',
    description: 'Attention-deficit hyperactivity disorder, other type',
    category: 'ADHD & Neurodevelopmental',
    synonyms: ['ADHD'],
  },
  {
    icd10: 'F90.9',
    description: 'Attention-deficit hyperactivity disorder, unspecified type',
    category: 'ADHD & Neurodevelopmental',
    synonyms: ['ADHD'],
  },
  {
    icd10: 'F91.3',
    description: 'Oppositional defiant disorder',
    category: 'ADHD & Neurodevelopmental',
    synonyms: ['ODD', 'oppositional defiant'],
  },
  {
    icd10: 'F95.2',
    description: "Tourette's disorder",
    category: 'ADHD & Neurodevelopmental',
    synonyms: ['Tourette'],
  },
  {
    icd10: 'F91.1',
    description: 'Conduct disorder, childhood-onset type',
    category: 'ADHD & Neurodevelopmental',
    synonyms: ['conduct disorder'],
  },
  {
    icd10: 'F91.2',
    description: 'Conduct disorder, adolescent-onset type',
    category: 'ADHD & Neurodevelopmental',
    synonyms: ['conduct disorder'],
  },
  {
    icd10: 'F91.9',
    description: 'Conduct disorder, unspecified',
    category: 'ADHD & Neurodevelopmental',
    synonyms: ['conduct disorder'],
  },
  {
    icd10: 'F93.8',
    description: 'Other childhood emotional disorders',
    category: 'ADHD & Neurodevelopmental',
  },
  {
    icd10: 'F94.0',
    description: 'Selective mutism',
    category: 'ADHD & Neurodevelopmental',
    synonyms: ['selective mutism'],
  },
  {
    icd10: 'F95.0',
    description: 'Transient tic disorder',
    category: 'ADHD & Neurodevelopmental',
    synonyms: ['tic'],
  },
  {
    icd10: 'F95.1',
    description: 'Chronic motor or vocal tic disorder',
    category: 'ADHD & Neurodevelopmental',
    synonyms: ['tic'],
  },
  {
    icd10: 'F84.2',
    description: "Rett's syndrome",
    category: 'ADHD & Neurodevelopmental',
    synonyms: ['Rett'],
  },
  {
    icd10: 'F84.3',
    description: 'Other childhood disintegrative disorder',
    category: 'ADHD & Neurodevelopmental',
  },
  {
    icd10: 'F84.8',
    description: 'Other pervasive developmental disorders',
    category: 'ADHD & Neurodevelopmental',
  },
  {
    icd10: 'F84.9',
    description: 'Pervasive developmental disorder, unspecified',
    category: 'ADHD & Neurodevelopmental',
  },
  {
    icd10: 'F80.81',
    description: 'Childhood onset fluency disorder',
    category: 'ADHD & Neurodevelopmental',
    synonyms: ['stuttering', 'fluency'],
  },
  {
    icd10: 'F80.82',
    description: 'Social pragmatic communication disorder',
    category: 'ADHD & Neurodevelopmental',
    synonyms: ['social pragmatic communication'],
  },
  {
    icd10: 'F81.0',
    description: 'Specific reading disorder',
    category: 'ADHD & Neurodevelopmental',
    synonyms: ['dyslexia', 'reading disorder'],
  },
  {
    icd10: 'F81.2',
    description: 'Mathematics disorder',
    category: 'ADHD & Neurodevelopmental',
    synonyms: ['math disorder', 'dyscalculia'],
  },
  {
    icd10: 'F98.0',
    description: 'Enuresis not due to a substance or known physiological condition',
    category: 'ADHD & Neurodevelopmental',
    synonyms: ['enuresis'],
  },
  {
    icd10: 'F98.1',
    description: 'Encopresis not due to a substance or known physiological condition',
    category: 'ADHD & Neurodevelopmental',
    synonyms: ['encopresis'],
  },
  {
    icd10: 'F98.4',
    description: 'Stereotyped movement disorders',
    category: 'ADHD & Neurodevelopmental',
    synonyms: ['stereotyped movement'],
  },
  // -----------------------
  // Sleep
  // -----------------------
  {
    icd10: 'G47.00',
    description: 'Insomnia, unspecified',
    category: 'Sleep',
    synonyms: ['sleep'],
  },
  {
    icd10: 'G47.10',
    description: 'Hypersomnia, unspecified',
    category: 'Sleep',
    synonyms: ['sleepy'],
  },
  {
    icd10: 'G47.33',
    description: 'Obstructive sleep apnea (adult)',
    category: 'Sleep',
    synonyms: ['OSA', 'sleep apnea'],
  },
  {
    icd10: 'F51.09',
    description: 'Other insomnia not due to a substance or known physiological condition',
    category: 'Sleep',
    synonyms: ['insomnia'],
  },
  {
    icd10: 'F51.11',
    description: 'Primary hypersomnia',
    category: 'Sleep',
    synonyms: ['hypersomnia'],
  },
  {
    icd10: 'F51.12',
    description: 'Insufficient sleep syndrome',
    category: 'Sleep',
  },
  {
    icd10: 'F51.3',
    description: 'Sleepwalking [somnambulism]',
    category: 'Sleep',
    synonyms: ['sleepwalking'],
  },
  {
    icd10: 'F51.4',
    description: 'Sleep terrors [night terrors]',
    category: 'Sleep',
    synonyms: ['night terrors'],
  },
  {
    icd10: 'F51.5',
    description: 'Nightmare disorder',
    category: 'Sleep',
    synonyms: ['nightmare'],
  },
  // -----------------------
  // OCD & Related
  // -----------------------
  {
    icd10: 'F42.9',
    description: 'Obsessive-compulsive disorder, unspecified',
    category: 'OCD & Related',
    synonyms: ['OCD'],
  },
  {
    icd10: 'F42.3',
    description: 'Hoarding disorder',
    category: 'OCD & Related',
    synonyms: ['hoarding'],
  },
  {
    icd10: 'F42.4',
    description: 'Excoriation (skin-picking) disorder',
    category: 'OCD & Related',
    synonyms: ['skin picking'],
  },
  {
    icd10: 'F45.22',
    description: 'Body dysmorphic disorder',
    category: 'OCD & Related',
    synonyms: ['BDD'],
  },
  {
    icd10: 'F63.3',
    description: 'Trichotillomania',
    category: 'OCD & Related',
    synonyms: ['hair pulling'],
  },
  {
    icd10: 'F42.8',
    description: 'Other obsessive-compulsive disorder',
    category: 'OCD & Related',
  },
  // -----------------------
  // Eating Disorders
  // -----------------------
  {
    icd10: 'F50.00',
    description: 'Anorexia nervosa, unspecified',
    category: 'Eating Disorders',
  },
  {
    icd10: 'F50.01',
    description: 'Anorexia nervosa, restricting type',
    category: 'Eating Disorders',
  },
  {
    icd10: 'F50.02',
    description: 'Anorexia nervosa, binge eating/purging type',
    category: 'Eating Disorders',
  },
  {
    icd10: 'F50.2',
    description: 'Bulimia nervosa',
    category: 'Eating Disorders',
  },
  {
    icd10: 'F50.9',
    description: 'Eating disorder, unspecified',
    category: 'Eating Disorders',
  },
  {
    icd10: 'F50.82',
    description: 'Avoidant/restrictive food intake disorder',
    category: 'Eating Disorders',
    synonyms: ['ARFID', 'avoidant restrictive'],
  },
  {
    icd10: 'F50.89',
    description: 'Other specified eating disorder',
    category: 'Eating Disorders',
    synonyms: ['OSFED'],
  },
  {
    icd10: 'F50.84',
    description: 'Rumination disorder in adults',
    category: 'Eating Disorders',
    synonyms: ['rumination'],
  },
  {
    icd10: 'F50.83',
    description: 'Pica in adults',
    category: 'Eating Disorders',
    synonyms: ['pica'],
  },
  // -----------------------
  // Personality Disorders
  // -----------------------
  {
    icd10: 'F60.0',
    description: 'Paranoid personality disorder',
    category: 'Personality Disorders',
  },
  {
    icd10: 'F60.1',
    description: 'Schizoid personality disorder',
    category: 'Personality Disorders',
  },
  {
    icd10: 'F60.2',
    description: 'Antisocial personality disorder',
    category: 'Personality Disorders',
  },
  {
    icd10: 'F60.4',
    description: 'Histrionic personality disorder',
    category: 'Personality Disorders',
  },
  {
    icd10: 'F60.5',
    description: 'Obsessive-compulsive personality disorder',
    category: 'Personality Disorders',
    synonyms: ['OCPD'],
  },
  {
    icd10: 'F60.6',
    description: 'Avoidant personality disorder',
    category: 'Personality Disorders',
  },
  {
    icd10: 'F60.7',
    description: 'Dependent personality disorder',
    category: 'Personality Disorders',
  },
  {
    icd10: 'F60.81',
    description: 'Narcissistic personality disorder',
    category: 'Personality Disorders',
  },
  {
    icd10: 'F60.89',
    description: 'Other specific personality disorders',
    category: 'Personality Disorders',
  },
  {
    icd10: 'F60.9',
    description: 'Personality disorder, unspecified',
    category: 'Personality Disorders',
  },
  // -----------------------
  // Substance Use - Alcohol
  // -----------------------
  {
    icd10: 'F10.10',
    description: 'Alcohol abuse, uncomplicated',
    category: 'Substance Use - Alcohol',
    synonyms: ['AUD mild'],
  },
  {
    icd10: 'F10.20',
    description: 'Alcohol dependence, uncomplicated',
    category: 'Substance Use - Alcohol',
    synonyms: ['AUD'],
  },
  {
    icd10: 'F10.21',
    description: 'Alcohol dependence, in remission',
    category: 'Substance Use - Alcohol',
    synonyms: ['AUD remission'],
    isModifierLike: true,
  },
  {
    icd10: 'F10.23',
    description: 'Alcohol dependence with withdrawal',
    category: 'Substance Use - Alcohol',
    synonyms: ['withdrawal'],
    isModifierLike: true,
  },
  {
    icd10: 'F10.24',
    description: 'Alcohol dependence with alcohol-induced mood disorder',
    category: 'Substance Use - Alcohol',
    isModifierLike: true,
  },
  {
    icd10: 'F10.90',
    description: 'Alcohol use, unspecified, uncomplicated',
    category: 'Substance Use - Alcohol',
  },
  {
    icd10: 'F10.91',
    description: 'Alcohol use, unspecified, in remission',
    category: 'Substance Use - Alcohol',
  },
  {
    icd10: 'F10.11',
    description: 'Alcohol abuse, in remission',
    category: 'Substance Use - Alcohol',
  },
  // -----------------------
  // Substance Use - Opioids
  // -----------------------
  {
    icd10: 'F11.10',
    description: 'Opioid abuse, uncomplicated',
    category: 'Substance Use - Opioids',
    synonyms: ['OUD mild'],
  },
  {
    icd10: 'F11.20',
    description: 'Opioid dependence, uncomplicated',
    category: 'Substance Use - Opioids',
    synonyms: ['OUD'],
  },
  {
    icd10: 'F11.21',
    description: 'Opioid dependence, in remission',
    category: 'Substance Use - Opioids',
    synonyms: ['OUD remission'],
    isModifierLike: true,
  },
  {
    icd10: 'F11.22',
    description: 'Opioid dependence with intoxication',
    category: 'Substance Use - Opioids',
    isModifierLike: true,
  },
  {
    icd10: 'F11.23',
    description: 'Opioid dependence with withdrawal',
    category: 'Substance Use - Opioids',
    synonyms: ['withdrawal'],
    isModifierLike: true,
  },
  {
    icd10: 'F11.24',
    description: 'Opioid dependence with opioid-induced mood disorder',
    category: 'Substance Use - Opioids',
    isModifierLike: true,
  },
  {
    icd10: 'F11.29',
    description: 'Opioid dependence with unspecified opioid-induced disorder',
    category: 'Substance Use - Opioids',
    isModifierLike: true,
  },
  {
    icd10: 'F11.90',
    description: 'Opioid use, unspecified, uncomplicated',
    category: 'Substance Use - Opioids',
  },
  {
    icd10: 'F11.91',
    description: 'Opioid use, unspecified, in remission',
    category: 'Substance Use - Opioids',
  },
  {
    icd10: 'F11.11',
    description: 'Opioid abuse, in remission',
    category: 'Substance Use - Opioids',
  },
  // -----------------------
  // Substance Use - Cannabis
  // -----------------------
  {
    icd10: 'F12.10',
    description: 'Cannabis abuse, uncomplicated',
    category: 'Substance Use - Cannabis',
  },
  {
    icd10: 'F12.20',
    description: 'Cannabis dependence, uncomplicated',
    category: 'Substance Use - Cannabis',
  },
  {
    icd10: 'F12.21',
    description: 'Cannabis dependence, in remission',
    category: 'Substance Use - Cannabis',
    isModifierLike: true,
  },
  {
    icd10: 'F12.22',
    description: 'Cannabis dependence with intoxication',
    category: 'Substance Use - Cannabis',
    isModifierLike: true,
  },
  {
    icd10: 'F12.23',
    description: 'Cannabis dependence with withdrawal',
    category: 'Substance Use - Cannabis',
    isModifierLike: true,
  },
  {
    icd10: 'F12.90',
    description: 'Cannabis use, unspecified, uncomplicated',
    category: 'Substance Use - Cannabis',
  },
  {
    icd10: 'F12.91',
    description: 'Cannabis use, unspecified, in remission',
    category: 'Substance Use - Cannabis',
  },
  {
    icd10: 'F12.11',
    description: 'Cannabis abuse, in remission',
    category: 'Substance Use - Cannabis',
  },
  // -----------------------
  // Substance Use - Stimulants
  // -----------------------
  {
    icd10: 'F15.10',
    description: 'Other stimulant abuse, uncomplicated',
    category: 'Substance Use - Stimulants',
    synonyms: ['amphetamine', 'meth'],
  },
  {
    icd10: 'F15.20',
    description: 'Other stimulant dependence, uncomplicated',
    category: 'Substance Use - Stimulants',
    synonyms: ['amphetamine', 'meth'],
  },
  {
    icd10: 'F15.21',
    description: 'Other stimulant dependence, in remission',
    category: 'Substance Use - Stimulants',
    isModifierLike: true,
  },
  {
    icd10: 'F15.23',
    description: 'Other stimulant dependence with withdrawal',
    category: 'Substance Use - Stimulants',
    isModifierLike: true,
  },
  {
    icd10: 'F14.10',
    description: 'Cocaine abuse, uncomplicated',
    category: 'Substance Use - Stimulants',
    synonyms: ['cocaine'],
  },
  {
    icd10: 'F14.20',
    description: 'Cocaine dependence, uncomplicated',
    category: 'Substance Use - Stimulants',
    synonyms: ['cocaine'],
  },
  {
    icd10: 'F14.21',
    description: 'Cocaine dependence, in remission',
    category: 'Substance Use - Stimulants',
    isModifierLike: true,
  },
  {
    icd10: 'F14.90',
    description: 'Cocaine use, unspecified, uncomplicated',
    category: 'Substance Use - Stimulants',
  },
  {
    icd10: 'F15.90',
    description: 'Other stimulant use, unspecified, uncomplicated',
    category: 'Substance Use - Stimulants',
  },
  {
    icd10: 'F15.93',
    description: 'Other stimulant use, unspecified with withdrawal',
    category: 'Substance Use - Stimulants',
  },
  // -----------------------
  // Substance Use - Sedatives
  // -----------------------
  {
    icd10: 'F13.10',
    description: 'Sedative, hypnotic, or anxiolytic abuse, uncomplicated',
    category: 'Substance Use - Sedatives',
    synonyms: ['benzo'],
  },
  {
    icd10: 'F13.20',
    description: 'Sedative, hypnotic, or anxiolytic dependence, uncomplicated',
    category: 'Substance Use - Sedatives',
    synonyms: ['benzo'],
  },
  {
    icd10: 'F13.21',
    description: 'Sedative, hypnotic, or anxiolytic dependence, in remission',
    category: 'Substance Use - Sedatives',
    isModifierLike: true,
  },
  {
    icd10: 'F13.23',
    description: 'Sedative, hypnotic, or anxiolytic dependence with withdrawal',
    category: 'Substance Use - Sedatives',
    isModifierLike: true,
  },
  {
    icd10: 'F13.90',
    description: 'Sedative, hypnotic, or anxiolytic use, unspecified, uncomplicated',
    category: 'Substance Use - Sedatives',
  },
  // -----------------------
  // Substance Use - Nicotine
  // -----------------------
  {
    icd10: 'F17.200',
    description: 'Nicotine dependence, unspecified, uncomplicated',
    category: 'Substance Use - Nicotine',
    synonyms: ['tobacco', 'nicotine'],
  },
  {
    icd10: 'F17.201',
    description: 'Nicotine dependence, unspecified, in remission',
    category: 'Substance Use - Nicotine',
    synonyms: ['remission'],
    isModifierLike: true,
  },
  {
    icd10: 'Z72.0',
    description: 'Tobacco use, not otherwise specified',
    category: 'Substance Use - Nicotine',
    synonyms: ['tobacco use'],
    isModifierLike: true,
  },
  // -----------------------
  // Substance Use - Other
  // -----------------------
  {
    icd10: 'F19.10',
    description: 'Other psychoactive substance abuse, uncomplicated',
    category: 'Substance Use - Other',
  },
  {
    icd10: 'F19.20',
    description: 'Other psychoactive substance dependence, uncomplicated',
    category: 'Substance Use - Other',
  },
  {
    icd10: 'F16.20',
    description: 'Hallucinogen dependence, uncomplicated',
    category: 'Substance Use - Other',
  },
  {
    icd10: 'F16.10',
    description: 'Hallucinogen abuse, uncomplicated',
    category: 'Substance Use - Other',
  },
  {
    icd10: 'F19.90',
    description: 'Other psychoactive substance use, unspecified, uncomplicated',
    category: 'Substance Use - Other',
  },
  // -----------------------
  // Dissociative
  // -----------------------
  {
    icd10: 'F44.81',
    description: 'Dissociative identity disorder',
    category: 'Dissociative',
    synonyms: ['DID'],
  },
  {
    icd10: 'F44.9',
    description: 'Dissociative and conversion disorder, unspecified',
    category: 'Dissociative',
  },
  {
    icd10: 'F48.1',
    description: 'Depersonalization-derealization disorder',
    category: 'Dissociative',
    synonyms: ['DPDR'],
  },
  {
    icd10: 'F44.0',
    description: 'Dissociative amnesia',
    category: 'Dissociative',
    synonyms: ['dissociative amnesia'],
  },
  {
    icd10: 'F44.1',
    description: 'Dissociative fugue',
    category: 'Dissociative',
    synonyms: ['dissociative fugue', 'fugue'],
  },
  {
    icd10: 'F44.89',
    description: 'Other dissociative and conversion disorders',
    category: 'Dissociative',
  },
  {
    icd10: 'F44.4',
    description: 'Conversion disorder with motor symptom or deficit',
    category: 'Dissociative',
    synonyms: ['conversion'],
  },
  {
    icd10: 'F44.5',
    description: 'Conversion disorder with seizures or convulsions',
    category: 'Dissociative',
    synonyms: ['conversion', 'PNES'],
  },
  {
    icd10: 'F44.6',
    description: 'Conversion disorder with sensory symptom or deficit',
    category: 'Dissociative',
    synonyms: ['conversion'],
  },
  {
    icd10: 'F44.7',
    description: 'Conversion disorder with mixed symptom presentation',
    category: 'Dissociative',
    synonyms: ['conversion'],
  },
  // -----------------------
  // Somatic
  // -----------------------
  {
    icd10: 'F45.1',
    description: 'Undifferentiated somatoform disorder',
    category: 'Somatic',
  },
  {
    icd10: 'F45.21',
    description: 'Illness anxiety disorder',
    category: 'Somatic',
    synonyms: ['health anxiety'],
  },
  {
    icd10: 'F45.41',
    description: 'Pain disorder exclusively related to psychological factors',
    category: 'Somatic',
    synonyms: ['pain disorder'],
  },
  {
    icd10: 'F45.42',
    description: 'Pain disorder with related psychological factors',
    category: 'Somatic',
    synonyms: ['pain disorder'],
  },
  {
    icd10: 'F45.0',
    description: 'Somatization disorder',
    category: 'Somatic',
    synonyms: ['somatization'],
  },
  {
    icd10: 'F45.9',
    description: 'Somatoform disorder, unspecified',
    category: 'Somatic',
    synonyms: ['somatoform'],
  },
  // -----------------------
  // Cognitive
  // -----------------------
  {
    icd10: 'R41.840',
    description: 'Attention and concentration deficit',
    category: 'Cognitive',
    synonyms: ['concentration'],
    isModifierLike: true,
  },
  {
    icd10: 'R41.89',
    description: 'Other symptoms and signs involving cognitive functions and awareness',
    category: 'Cognitive',
    isModifierLike: true,
  },
  {
    icd10: 'R41.0',
    description: 'Disorientation, unspecified',
    category: 'Cognitive',
    synonyms: ['disorientation'],
  },
  {
    icd10: 'R41.82',
    description: 'Altered mental status, unspecified',
    category: 'Cognitive',
    synonyms: ['altered mental status', 'AMS'],
  },
  {
    icd10: 'R41.9',
    description: 'Unspecified symptoms and signs involving cognitive functions and awareness',
    category: 'Cognitive',
    synonyms: ['cognitive symptom'],
  },
  {
    icd10: 'R41.1',
    description: 'Anterograde amnesia',
    category: 'Cognitive',
    synonyms: ['amnesia'],
  },
  {
    icd10: 'R41.2',
    description: 'Retrograde amnesia',
    category: 'Cognitive',
    synonyms: ['amnesia'],
  },
  {
    icd10: 'R41.3',
    description: 'Other amnesia',
    category: 'Cognitive',
    synonyms: ['amnesia'],
  },
  {
    icd10: 'R41.81',
    description: 'Age-related cognitive decline',
    category: 'Cognitive',
    synonyms: ['age-related cognitive decline'],
  },
  {
    icd10: 'R41.83',
    description: 'Borderline intellectual functioning',
    category: 'Cognitive',
    synonyms: ['borderline intellectual functioning'],
  },
  {
    icd10: 'R41.844',
    description: 'Frontal lobe and executive function deficit',
    category: 'Cognitive',
    synonyms: ['executive dysfunction'],
  },
  {
    icd10: 'R41.841',
    description: 'Cognitive communication deficit',
    category: 'Cognitive',
  },
  {
    icd10: 'R41.842',
    description: 'Visuospatial deficit',
    category: 'Cognitive',
  },
  {
    icd10: 'R41.843',
    description: 'Psychomotor deficit',
    category: 'Cognitive',
  },
  {
    icd10: 'R41.85',
    description: 'Anosognosia',
    category: 'Cognitive',
    synonyms: ['anosognosia'],
  },
  {
    icd10: 'F06.31',
    description: 'Mood disorder due to known physiological condition with depressive features',
    category: 'Cognitive',
  },
  {
    icd10: 'F06.32',
    description: 'Mood disorder due to known physiological condition with major depressive-like episode',
    category: 'Cognitive',
  },
  {
    icd10: 'F06.30',
    description: 'Mood disorder due to known physiological condition, unspecified',
    category: 'Cognitive',
  },
  {
    icd10: 'F06.4',
    description: 'Anxiety disorder due to known physiological condition',
    category: 'Cognitive',
  },
  {
    icd10: 'F07.81',
    description: 'Postconcussional syndrome',
    category: 'Cognitive',
    synonyms: ['postconcussional', 'post-concussion'],
  },
  {
    icd10: 'F07.0',
    description: 'Personality change due to known physiological condition',
    category: 'Cognitive',
  },
  {
    icd10: 'F09',
    description: 'Unspecified mental disorder due to known physiological condition',
    category: 'Cognitive',
  },
  {
    icd10: 'F48.2',
    description: 'Pseudobulbar affect',
    category: 'Cognitive',
    synonyms: ['pseudobulbar affect', 'PBA'],
  },
  {
    icd10: 'F06.33',
    description: 'Mood disorder due to known physiological condition with manic features',
    category: 'Cognitive',
  },
  {
    icd10: 'F06.34',
    description: 'Mood disorder due to known physiological condition with mixed features',
    category: 'Cognitive',
  },
  {
    icd10: 'F06.8',
    description: 'Other specified mental disorders due to known physiological condition',
    category: 'Cognitive',
  },
  {
    icd10: 'F07.89',
    description: 'Other personality and behavioral disorders due to known physiological condition',
    category: 'Cognitive',
  },
  {
    icd10: 'F01.50',
    description: 'Vascular dementia, unspecified severity, without behavioral disturbance, psychotic disturbance, mood disturbance, and anxiety',
    category: 'Cognitive',
    synonyms: ['vascular dementia'],
  },
  {
    icd10: 'F02.80',
    description: 'Dementia in other diseases classified elsewhere, unspecified severity, without behavioral disturbance, psychotic disturbance, mood disturbance, and anxiety',
    category: 'Cognitive',
    synonyms: ['dementia'],
  },
  {
    icd10: 'F03.90',
    description: 'Unspecified dementia, unspecified severity, without behavioral disturbance, psychotic disturbance, mood disturbance, and anxiety',
    category: 'Cognitive',
    synonyms: ['dementia'],
  },
  // -----------------------
  // Behavioral & Other Mental
  // -----------------------
  {
    icd10: 'F64.0',
    description: 'Transsexualism',
    category: 'Behavioral & Other Mental',
    synonyms: ['gender dysphoria (icd10-era)'],
  },
  {
    icd10: 'F59',
    description: 'Unspecified behavioral syndromes associated with physiological disturbances and physical factors',
    category: 'Behavioral & Other Mental',
  },
  {
    icd10: 'F48.8',
    description: 'Other specified nonpsychotic mental disorders',
    category: 'Behavioral & Other Mental',
  },
  {
    icd10: 'F48.9',
    description: 'Nonpsychotic mental disorder, unspecified',
    category: 'Behavioral & Other Mental',
  },
  {
    icd10: 'F99',
    description: 'Mental disorder, not otherwise specified',
    category: 'Behavioral & Other Mental',
  },
  // -----------------------
  // Symptoms (R-codes)
  // -----------------------
  {
    icd10: 'R45.851',
    description: 'Suicidal ideations',
    category: 'Symptoms (R-codes)',
    synonyms: ['SI', 'suicidal ideation', 'suicidality'],
  },
  {
    icd10: 'R45.850',
    description: 'Homicidal ideations',
    category: 'Symptoms (R-codes)',
    synonyms: ['HI', 'homicidal ideation'],
  },
  {
    icd10: 'R45.84',
    description: 'Anhedonia',
    category: 'Symptoms (R-codes)',
    synonyms: ['anhedonia'],
  },
  {
    icd10: 'R45.0',
    description: 'Nervousness',
    category: 'Symptoms (R-codes)',
    synonyms: ['nervousness'],
  },
  {
    icd10: 'R45.1',
    description: 'Restlessness and agitation',
    category: 'Symptoms (R-codes)',
    synonyms: ['agitation', 'restlessness'],
  },
  {
    icd10: 'R45.4',
    description: 'Irritability and anger',
    category: 'Symptoms (R-codes)',
    synonyms: ['irritability', 'anger'],
  },
  {
    icd10: 'R45.86',
    description: 'Emotional lability',
    category: 'Symptoms (R-codes)',
    synonyms: ['emotional lability', 'mood swings'],
  },
  {
    icd10: 'R45.87',
    description: 'Impulsiveness',
    category: 'Symptoms (R-codes)',
    synonyms: ['impulsiveness', 'impulsivity'],
  },
  {
    icd10: 'R44.0',
    description: 'Auditory hallucinations',
    category: 'Symptoms (R-codes)',
    synonyms: ['auditory hallucinations', 'hearing voices'],
  },
  {
    icd10: 'R44.1',
    description: 'Visual hallucinations',
    category: 'Symptoms (R-codes)',
    synonyms: ['visual hallucinations'],
  },
  {
    icd10: 'R44.3',
    description: 'Hallucinations, unspecified',
    category: 'Symptoms (R-codes)',
    synonyms: ['hallucinations'],
  },
  {
    icd10: 'R45.89',
    description: 'Other symptoms and signs involving emotional state',
    category: 'Symptoms (R-codes)',
  },
  {
    icd10: 'R45.2',
    description: 'Unhappiness',
    category: 'Symptoms (R-codes)',
    synonyms: ['unhappiness'],
  },
  {
    icd10: 'R45.3',
    description: 'Demoralization and apathy',
    category: 'Symptoms (R-codes)',
    synonyms: ['demoralization', 'apathy'],
  },
  {
    icd10: 'R45.5',
    description: 'Hostility',
    category: 'Symptoms (R-codes)',
    synonyms: ['hostility'],
  },
  {
    icd10: 'R45.6',
    description: 'Violent behavior',
    category: 'Symptoms (R-codes)',
    synonyms: ['violent behavior'],
  },
  {
    icd10: 'R45.7',
    description: 'State of emotional shock and stress, unspecified',
    category: 'Symptoms (R-codes)',
    synonyms: ['emotional shock', 'acute stress'],
  },
  {
    icd10: 'R45.81',
    description: 'Low self-esteem',
    category: 'Symptoms (R-codes)',
    synonyms: ['low self-esteem'],
  },
  {
    icd10: 'R45.82',
    description: 'Worries',
    category: 'Symptoms (R-codes)',
    synonyms: ['worries'],
  },
  {
    icd10: 'R45.88',
    description: 'Nonsuicidal self-harm',
    category: 'Symptoms (R-codes)',
    synonyms: ['nonsuicidal self-harm', 'NSSI', 'self harm'],
  },
  {
    icd10: 'R44.2',
    description: 'Other hallucinations',
    category: 'Symptoms (R-codes)',
    synonyms: ['hallucinations'],
  },
  {
    icd10: 'R46.1',
    description: 'Bizarre personal appearance',
    category: 'Symptoms (R-codes)',
  },
  {
    icd10: 'R46.2',
    description: 'Strange and inexplicable behavior',
    category: 'Symptoms (R-codes)',
  },
  {
    icd10: 'R46.3',
    description: 'Overactivity',
    category: 'Symptoms (R-codes)',
    synonyms: ['overactivity'],
  },
  {
    icd10: 'R46.4',
    description: 'Slowness and poor responsiveness',
    category: 'Symptoms (R-codes)',
    synonyms: ['psychomotor retardation'],
  },
  {
    icd10: 'R46.5',
    description: 'Suspiciousness and marked evasiveness',
    category: 'Symptoms (R-codes)',
    synonyms: ['suspiciousness', 'paranoia'],
  },
  {
    icd10: 'R46.81',
    description: 'Obsessive-compulsive behavior',
    category: 'Symptoms (R-codes)',
    synonyms: ['obsessive-compulsive behavior'],
  },
  {
    icd10: 'R46.89',
    description: 'Other symptoms and signs involving appearance and behavior',
    category: 'Symptoms (R-codes)',
  },
  {
    icd10: 'R63.0',
    description: 'Anorexia',
    category: 'Symptoms (R-codes)',
    synonyms: ['appetite loss'],
  },
  {
    icd10: 'R40.0',
    description: 'Somnolence',
    category: 'Symptoms (R-codes)',
    synonyms: ['somnolence', 'hypersomnia'],
  },
  {
    icd10: 'R45.83',
    description: 'Excessive crying of child, adolescent or adult',
    category: 'Symptoms (R-codes)',
    synonyms: ['excessive crying'],
  },
  {
    icd10: 'R44.8',
    description: 'Other symptoms and signs involving general sensations and perceptions',
    category: 'Symptoms (R-codes)',
  },
  {
    icd10: 'R44.9',
    description: 'Unspecified symptoms and signs involving general sensations and perceptions',
    category: 'Symptoms (R-codes)',
  },
  {
    icd10: 'R46.0',
    description: 'Very low level of personal hygiene',
    category: 'Symptoms (R-codes)',
  },
  {
    icd10: 'R46.6',
    description: 'Undue concern and preoccupation with stressful events',
    category: 'Symptoms (R-codes)',
  },
  {
    icd10: 'R46.7',
    description: 'Verbosity and circumstantial detail obscuring reason for contact',
    category: 'Symptoms (R-codes)',
  },
  {
    icd10: 'R40.1',
    description: 'Stupor',
    category: 'Symptoms (R-codes)',
    synonyms: ['stupor'],
  },
  {
    icd10: 'R51.9',
    description: 'Headache, unspecified',
    category: 'Symptoms (R-codes)',
    synonyms: ['headache'],
  },
  // -----------------------
  // Z-Codes (Medication/Adherence)
  // -----------------------
  {
    icd10: 'Z79.899',
    description: 'Other long term (current) drug therapy',
    category: 'Z-Codes (Medication/Adherence)',
    synonyms: ['long-term medication'],
    isModifierLike: true,
  },
  {
    icd10: 'Z91.14',
    description: "Patient's other noncompliance with medication regimen",
    category: 'Z-Codes (Medication/Adherence)',
    synonyms: ['nonadherence', 'noncompliance'],
    isModifierLike: true,
  },
  {
    icd10: 'Z91.148',
    description: "Patient's other noncompliance with other medical treatment and regimen",
    category: 'Z-Codes (Medication/Adherence)',
    synonyms: ['nonadherence'],
    isModifierLike: true,
  },
  {
    icd10: 'Z81.8',
    description: 'Family history of other mental and behavioral disorders',
    category: 'Z-Codes (Medication/Adherence)',
    isModifierLike: true,
  },
  {
    icd10: 'Z86.59',
    description: 'Personal history of other mental and behavioral disorders',
    category: 'Z-Codes (Medication/Adherence)',
    isModifierLike: true,
  },
  // -----------------------
  // Z-Codes (Psychosocial/Context)
  // -----------------------
  {
    icd10: 'Z60.0',
    description: 'Problems of adjustment to life-cycle transitions',
    category: 'Z-Codes (Psychosocial/Context)',
    isModifierLike: true,
  },
  {
    icd10: 'Z60.2',
    description: 'Problems related to living alone',
    category: 'Z-Codes (Psychosocial/Context)',
    isModifierLike: true,
  },
  {
    icd10: 'Z60.4',
    description: 'Social exclusion and rejection',
    category: 'Z-Codes (Psychosocial/Context)',
    isModifierLike: true,
  },
  {
    icd10: 'Z65.4',
    description: 'Victim of crime and terrorism',
    category: 'Z-Codes (Psychosocial/Context)',
    synonyms: ['victim of crime'],
  },
  {
    icd10: 'Z65.5',
    description: 'Exposure to disaster, war and other hostilities',
    category: 'Z-Codes (Psychosocial/Context)',
  },
  {
    icd10: 'Z60.3',
    description: 'Acculturation difficulty',
    category: 'Z-Codes (Psychosocial/Context)',
    synonyms: ['acculturation'],
  },
  {
    icd10: 'Z60.5',
    description: 'Target of (perceived) adverse discrimination and persecution',
    category: 'Z-Codes (Psychosocial/Context)',
    synonyms: ['discrimination'],
  },
  {
    icd10: 'Z73.3',
    description: 'Stress, not elsewhere classified',
    category: 'Z-Codes (Psychosocial/Context)',
    synonyms: ['stress'],
  },
  {
    icd10: 'Z73.89',
    description: 'Other problems related to life management difficulty',
    category: 'Z-Codes (Psychosocial/Context)',
  },
  {
    icd10: 'Z73.4',
    description: 'Inadequate social skills, not elsewhere classified',
    category: 'Z-Codes (Psychosocial/Context)',
  },
  {
    icd10: 'Z73.6',
    description: 'Limitation of activities due to disability',
    category: 'Z-Codes (Psychosocial/Context)',
  },
  {
    icd10: 'Z72.89',
    description: 'Other problems related to lifestyle',
    category: 'Z-Codes (Psychosocial/Context)',
  },
  {
    icd10: 'Z72.810',
    description: 'Child and adolescent antisocial behavior',
    category: 'Z-Codes (Psychosocial/Context)',
  },
  {
    icd10: 'Z72.811',
    description: 'Adult antisocial behavior',
    category: 'Z-Codes (Psychosocial/Context)',
  },
  {
    icd10: 'Z87.890',
    description: 'Personal history of sex reassignment',
    category: 'Z-Codes (Psychosocial/Context)',
    synonyms: ['sex reassignment', 'gender'],
  },
  {
    icd10: 'Z64.4',
    description: 'Discord with counselors',
    category: 'Z-Codes (Psychosocial/Context)',
  },
  // -----------------------
  // Z-Codes (Family/Relationship)
  // -----------------------
  {
    icd10: 'Z62.820',
    description: 'Parent-child relational problem',
    category: 'Z-Codes (Family/Relationship)',
    isModifierLike: true,
  },
  {
    icd10: 'Z63.0',
    description: 'Problems in relationship with spouse or partner',
    category: 'Z-Codes (Family/Relationship)',
    synonyms: ['relationship conflict'],
    isModifierLike: true,
  },
  {
    icd10: 'Z63.4',
    description: 'Disappearance and death of family member',
    category: 'Z-Codes (Family/Relationship)',
    synonyms: ['bereavement', 'grief'],
    isModifierLike: true,
  },
  {
    icd10: 'Z63.5',
    description: 'Disruption of family by separation and divorce',
    category: 'Z-Codes (Family/Relationship)',
    isModifierLike: true,
  },
  {
    icd10: 'Z63.72',
    description: 'Alcoholism and drug addiction in family',
    category: 'Z-Codes (Family/Relationship)',
    synonyms: ['family addiction'],
  },
  {
    icd10: 'Z63.79',
    description: 'Other stressful life events affecting family and household',
    category: 'Z-Codes (Family/Relationship)',
  },
  {
    icd10: 'Z63.8',
    description: 'Other specified problems related to primary support group',
    category: 'Z-Codes (Family/Relationship)',
  },
  {
    icd10: 'Z63.1',
    description: 'Problems in relationship with in-laws',
    category: 'Z-Codes (Family/Relationship)',
  },
  {
    icd10: 'Z64.0',
    description: 'Problems related to unwanted pregnancy',
    category: 'Z-Codes (Family/Relationship)',
  },
  // -----------------------
  // Z-Codes (Abuse/Trauma History)
  // -----------------------
  {
    icd10: 'Z62.810',
    description: 'Personal history of physical and sexual abuse in childhood',
    category: 'Z-Codes (Abuse/Trauma History)',
  },
  {
    icd10: 'Z62.811',
    description: 'Personal history of psychological abuse in childhood',
    category: 'Z-Codes (Abuse/Trauma History)',
  },
  {
    icd10: 'Z62.812',
    description: 'Personal history of neglect in childhood',
    category: 'Z-Codes (Abuse/Trauma History)',
  },
  {
    icd10: 'Z62.813',
    description: 'Personal history of forced labor or sexual exploitation in childhood',
    category: 'Z-Codes (Abuse/Trauma History)',
  },
  {
    icd10: 'Z62.819',
    description: 'Personal history of unspecified abuse in childhood',
    category: 'Z-Codes (Abuse/Trauma History)',
  },
  {
    icd10: 'Z91.410',
    description: 'Personal history of adult physical and sexual abuse',
    category: 'Z-Codes (Abuse/Trauma History)',
  },
  {
    icd10: 'Z91.411',
    description: 'Personal history of adult psychological abuse',
    category: 'Z-Codes (Abuse/Trauma History)',
  },
  {
    icd10: 'Z91.412',
    description: 'Personal history of adult neglect',
    category: 'Z-Codes (Abuse/Trauma History)',
  },
  {
    icd10: 'Z91.414',
    description: 'Personal history of adult intimate partner abuse',
    category: 'Z-Codes (Abuse/Trauma History)',
    synonyms: ['IPV', 'intimate partner'],
  },
  {
    icd10: 'Z91.419',
    description: 'Personal history of unspecified adult abuse',
    category: 'Z-Codes (Abuse/Trauma History)',
  },
  {
    icd10: 'Z91.49',
    description: 'Other personal history of psychological trauma, not elsewhere classified',
    category: 'Z-Codes (Abuse/Trauma History)',
    synonyms: ['trauma history'],
  },
  {
    icd10: 'Z91.51',
    description: 'Personal history of suicidal behavior',
    category: 'Z-Codes (Abuse/Trauma History)',
    synonyms: ['suicide history', 'suicidal behavior history'],
  },
  {
    icd10: 'Z91.52',
    description: 'Personal history of nonsuicidal self-harm',
    category: 'Z-Codes (Abuse/Trauma History)',
    synonyms: ['self-harm history', 'NSSI history'],
  },
  {
    icd10: 'Z69.010',
    description: 'Encounter for mental health services for victim of parental child abuse',
    category: 'Z-Codes (Abuse/Trauma History)',
  },
  {
    icd10: 'Z69.011',
    description: 'Encounter for mental health services for perpetrator of parental child abuse',
    category: 'Z-Codes (Abuse/Trauma History)',
  },
  {
    icd10: 'Z69.11',
    description: 'Encounter for mental health services for victim of spousal or partner abuse',
    category: 'Z-Codes (Abuse/Trauma History)',
  },
  {
    icd10: 'Z69.12',
    description: 'Encounter for mental health services for perpetrator of spousal or partner abuse',
    category: 'Z-Codes (Abuse/Trauma History)',
  },
  {
    icd10: 'Z69.81',
    description: 'Encounter for mental health services for victim of other abuse',
    category: 'Z-Codes (Abuse/Trauma History)',
  },
  {
    icd10: 'Z69.82',
    description: 'Encounter for mental health services for perpetrator of other abuse',
    category: 'Z-Codes (Abuse/Trauma History)',
  },
  // -----------------------
  // Z-Codes (Work/School)
  // -----------------------
  {
    icd10: 'Z56.0',
    description: 'Unemployment, unspecified',
    category: 'Z-Codes (Work/School)',
    isModifierLike: true,
  },
  {
    icd10: 'Z56.3',
    description: 'Stressful work schedule',
    category: 'Z-Codes (Work/School)',
    isModifierLike: true,
  },
  {
    icd10: 'Z56.6',
    description: 'Other physical and mental strain related to work',
    category: 'Z-Codes (Work/School)',
    isModifierLike: true,
  },
  {
    icd10: 'Z55.9',
    description: 'Problems related to education and literacy, unspecified',
    category: 'Z-Codes (Work/School)',
    isModifierLike: true,
  },
  {
    icd10: 'Z73.0',
    description: 'Burn-out',
    category: 'Z-Codes (Work/School)',
    synonyms: ['burnout', 'burn-out'],
  },
  // -----------------------
  // Z-Codes (Housing/Econ)
  // -----------------------
  {
    icd10: 'Z59.0',
    description: 'Homelessness',
    category: 'Z-Codes (Housing/Econ)',
    isModifierLike: true,
  },
  {
    icd10: 'Z59.1',
    description: 'Inadequate housing',
    category: 'Z-Codes (Housing/Econ)',
    isModifierLike: true,
  },
  {
    icd10: 'Z59.6',
    description: 'Low income',
    category: 'Z-Codes (Housing/Econ)',
    isModifierLike: true,
  },
  {
    icd10: 'Z59.7',
    description: 'Insufficient social insurance and welfare support',
    category: 'Z-Codes (Housing/Econ)',
    isModifierLike: true,
  },
  // -----------------------
  // Z-Codes (Legal/Forensic)
  // -----------------------
  {
    icd10: 'Z65.3',
    description: 'Problems related to other legal circumstances',
    category: 'Z-Codes (Legal/Forensic)',
    isModifierLike: true,
  },
  {
    icd10: 'Z65.8',
    description: 'Other specified problems related to psychosocial circumstances',
    category: 'Z-Codes (Legal/Forensic)',
    isModifierLike: true,
  },
  {
    icd10: 'Z65.0',
    description: 'Conviction in civil and criminal proceedings without imprisonment',
    category: 'Z-Codes (Legal/Forensic)',
  },
  {
    icd10: 'Z65.1',
    description: 'Imprisonment and other incarceration',
    category: 'Z-Codes (Legal/Forensic)',
    synonyms: ['incarceration'],
  },
  {
    icd10: 'Z65.2',
    description: 'Problems related to release from prison',
    category: 'Z-Codes (Legal/Forensic)',
  },
  // -----------------------
  // Z-Codes (Other)
  // -----------------------
  {
    icd10: 'Z91.89',
    description: 'Other specified personal risk factors, not elsewhere classified',
    category: 'Z-Codes (Other)',
    isModifierLike: true,
  },
  {
    icd10: 'Z71.89',
    description: 'Other specified counseling',
    category: 'Z-Codes (Other)',
    isModifierLike: true,
  },
  {
    icd10: 'Z71.41',
    description: 'Alcohol abuse counseling and surveillance of alcoholic',
    category: 'Z-Codes (Other)',
    synonyms: ['alcohol counseling'],
  },
  {
    icd10: 'Z71.51',
    description: 'Drug abuse counseling and surveillance of drug abuser',
    category: 'Z-Codes (Other)',
    synonyms: ['drug counseling'],
  },
  {
    icd10: 'Z71.42',
    description: 'Counseling for family member of alcoholic',
    category: 'Z-Codes (Other)',
  },
  {
    icd10: 'Z71.52',
    description: 'Counseling for family member of drug abuser',
    category: 'Z-Codes (Other)',
  },
  {
    icd10: 'Z71.9',
    description: 'Counseling, unspecified',
    category: 'Z-Codes (Other)',
    synonyms: ['counseling'],
  },
  {
    icd10: 'Z70.9',
    description: 'Sex counseling, unspecified',
    category: 'Z-Codes (Other)',
  },
  {
    icd10: 'Z70.2',
    description: 'Counseling related to sexual behavior and orientation of third party',
    category: 'Z-Codes (Other)',
  },
  {
    icd10: 'Z13.31',
    description: 'Encounter for screening for depression',
    category: 'Z-Codes (Other)',
    synonyms: ['depression screening', 'PHQ'],
  },
  {
    icd10: 'Z13.30',
    description: 'Encounter for screening examination for mental health and behavioral disorders, unspecified',
    category: 'Z-Codes (Other)',
  },
  {
    icd10: 'Z13.39',
    description: 'Encounter for screening examination for other mental health and behavioral disorders',
    category: 'Z-Codes (Other)',
  },
  {
    icd10: 'Z03.89',
    description: 'Encounter for observation for other suspected diseases and conditions ruled out',
    category: 'Z-Codes (Other)',
    synonyms: ['rule out'],
  },
  {
    icd10: 'Z76.5',
    description: 'Malingerer [conscious simulation]',
    category: 'Z-Codes (Other)',
    synonyms: ['malingering'],
  },
];

export interface ICD10CodePickerProps {
  /**
   * Selected ICD-10 codes (first is primary, up to 4 total)
   * Can also accept a single string for backwards compatibility
   */
  value?: string[] | string;
  /**
   * Callback when codes change
   */
  onChange: (codes: string[]) => void;
  /**
   * Optional callback when Enter key is pressed
   */
  onEnter?: () => void;
  /**
   * Whether the input is disabled
   */
  disabled?: boolean;
  /**
   * Whether to show the header with description and info tooltip
   * @default true
   */
  showHeader?: boolean;
  /**
   * Size of the TextField input
   * @default 'small'
   */
  size?: 'small' | 'medium';
  /**
   * Maximum number of codes allowed
   * @default 4
   */
  maxCodes?: number;
  /**
   * Provenance for each diagnosis code, keyed by normalized ICD-10 code.
   * When provided, a small source indicator (AI / Provider) is shown on each chip.
   */
  diagnosisCodeSources?: BillingCodeSourceMap;
  /**
   * When true, render a compact add button until the user opens the autocomplete.
   */
  hideInputUntilActive?: boolean;
  /**
   * When true, place the compact add action beside selected chips when space allows.
   */
  inlineAddAction?: boolean;
}

const ICD10_INFO_TOOLTIP =
  'Select ICD-10 diagnosis codes for this encounter. The first code is the primary diagnosis. You can add up to 3 additional codes.';

const MAX_CODES_DEFAULT = 4;

/**
 * ICD-10 Code Picker component with autocomplete
 *
 * Features:
 * - Autocomplete with common psychiatric ICD-10 codes
 * - Supports multiple codes (first is primary, up to 3 additional)
 * - Grouped by category for easy browsing
 * - Supports synonyms for better search
 * - freeSolo mode allows custom codes
 *
 * @example
 * ```tsx
 * <ICD10CodePicker
 *   value={icd10Codes}
 *   onChange={(codes) => setIcd10Codes(codes)}
 *   onEnter={() => handleSave()}
 * />
 * ```
 */
export const ICD10CodePicker: React.FC<ICD10CodePickerProps> = ({
  value = [],
  onChange,
  onEnter,
  disabled = false,
  showHeader = true,
  size = 'small',
  maxCodes = MAX_CODES_DEFAULT,
  diagnosisCodeSources,
  hideInputUntilActive = false,
  inlineAddAction = false,
}) => {
  const theme = useTheme();

  // Normalize value to array
  const codes = useMemo(() => {
    if (typeof value === 'string') {
      return value ? [value] : [];
    }
    return value.filter(c => c && c.trim() !== '');
  }, [value]);

  // State for the autocomplete input
  const [inputValue, setInputValue] = React.useState('');
  const [isInputActive, setInputActive] = React.useState(!hideInputUntilActive);

  // Single-value pickers (maxCodes === 1) behave as a replace-in-place field:
  // the search box stays visible even when a code is selected, and picking a new
  // option swaps the existing one instead of being blocked once the cap is hit.
  const isSingleSelect = maxCodes === 1;

  // Check if we can add more codes
  const canAddMore = codes.length < maxCodes;

  // Add a code
  const handleAddCode = useCallback(
    (code: string) => {
      if (!code || codes.includes(code)) return;
      if (isSingleSelect) {
        // Replace the current selection rather than appending.
        onChange([code]);
      } else {
        if (!canAddMore) return;
        onChange([...codes, code]);
      }
      setInputValue('');
      if (hideInputUntilActive) {
        setInputActive(false);
      }
    },
    [codes, onChange, canAddMore, isSingleSelect, hideInputUntilActive],
  );

  // Remove a code
  const handleRemoveCode = useCallback(
    (codeToRemove: string) => {
      onChange(codes.filter(c => c !== codeToRemove));
    },
    [codes, onChange],
  );

  // Make a code primary (move to first position)
  const handleMakePrimary = useCallback(
    (code: string) => {
      const filtered = codes.filter(c => c !== code);
      onChange([code, ...filtered]);
    },
    [codes, onChange],
  );

  const handleChange = useCallback(
    (_event: React.SyntheticEvent, newValue: ICD10CodeOption | string | null) => {
      let icd10Code = '';
      if (typeof newValue === 'string') {
        // freeSolo: user typed a custom value
        icd10Code = newValue
          .toUpperCase()
          .replace(/[^A-Z0-9.-]/g, '')
          .slice(0, 10);
      } else if (newValue) {
        icd10Code = newValue.icd10;
      }
      if (icd10Code) {
        handleAddCode(icd10Code);
      }
    },
    [handleAddCode],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        const target = e.target as HTMLInputElement;
        const isDropdownOpen = target.getAttribute('aria-expanded') === 'true';
        if (!isDropdownOpen) {
          // If there's input text, add it as a code
          if (inputValue.trim()) {
            e.preventDefault();
            const cleaned = inputValue
              .toUpperCase()
              .replace(/[^A-Z0-9.-]/g, '')
              .slice(0, 10);
            if (cleaned) {
              handleAddCode(cleaned);
            }
          } else if (onEnter) {
            e.preventDefault();
            onEnter();
          }
        }
      }
    },
    [inputValue, handleAddCode, onEnter],
  );

  // Custom option rendering
  const renderOption = (props: React.HTMLAttributes<HTMLLIElement>, option: ICD10CodeOption) => {
    if (option.__custom) {
      return (
        <Box
          component="li"
          {...props}
          key={`custom-${option.icd10}`}
          sx={{
            borderTop: `1px solid ${theme.palette.divider}`,
            fontStyle: 'italic',
          }}
        >
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
            <AddIcon sx={{ fontSize: 18, color: theme.palette.primary.main }} />
            <Typography variant="body2" sx={{ color: theme.palette.primary.main, fontWeight: 500 }}>
              Add custom code:{' '}
              <Typography
                component="span"
                variant="body2"
                sx={{ fontFamily: 'monospace', fontWeight: 700 }}
              >
                {option.icd10}
              </Typography>
            </Typography>
          </Stack>
        </Box>
      );
    }

    return (
      <Box component="li" {...props} key={option.icd10}>
        <Stack direction="row" spacing={1} sx={{ alignItems: 'flex-start' }}>
          <Typography
            variant="body2"
            sx={{
              fontFamily: 'monospace',
              fontWeight: 600,
              minWidth: 80,
              color: theme.palette.text.primary,
            }}
          >
            {option.icd10}
          </Typography>
          <Box sx={{ flex: 1 }}>
            <Typography variant="body2" sx={{ color: theme.palette.text.primary }}>
              {option.description}
            </Typography>
            {option.category !== 'Common' && (
              <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                {option.category}
              </Typography>
            )}
          </Box>
        </Stack>
      </Box>
    );
  };

  // Filter options based on search (including synonyms) and exclude already selected
  const filterOptions = useCallback(
    (options: ICD10CodeOption[], { inputValue: searchInput }: { inputValue: string }) => {
      const searchLower = searchInput.toLowerCase().trim();

      // First filter out already selected codes
      let filtered = options.filter(option => !codes.includes(option.icd10));

      // Then filter by search if provided
      if (searchLower) {
        filtered = filtered.filter(option => {
          // Match ICD-10 code
          if (option.icd10.toLowerCase().includes(searchLower)) return true;
          // Match description
          if (option.description.toLowerCase().includes(searchLower)) return true;
          // Match synonyms
          if (option.synonyms?.some(syn => syn.toLowerCase().includes(searchLower))) return true;
          return false;
        });
      }

      // Append a synthetic "Add custom code" row when the input looks like a valid
      // ICD-10 code but has no exact match in the list and isn't already selected.
      if (searchLower) {
        const cleaned = searchInput
          .toUpperCase()
          .replace(/[^A-Z0-9.-]/g, '')
          .slice(0, 10);
        const looksLikeIcd10 = /^[A-Z]\d{2}(\.\d{1,4})?$/.test(cleaned);
        const exactMatchExists = options.some(
          opt => opt.icd10.toLowerCase() === cleaned.toLowerCase(),
        );
        const alreadySelected = codes.includes(cleaned);

        if (looksLikeIcd10 && !exactMatchExists && !alreadySelected) {
          filtered.push({
            icd10: cleaned,
            description: 'Add custom code',
            category: 'Common',
            __custom: true,
          });
        }
      }

      return filtered;
    },
    [codes],
  );

  // Group options by category (custom options get an empty group so they appear ungrouped)
  const groupBy = useCallback(
    (option: ICD10CodeOption) => (option.__custom ? '' : option.category),
    [],
  );

  // Get description for a code
  const getCodeDescription = useCallback((code: string) => {
    const found = ICD10_CODES.find(opt => opt.icd10 === code);
    return found?.description || '';
  }, []);

  return (
    <Stack spacing={1.5}>
      {showHeader && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            ICD-10 Codes
          </Typography>
          <Tooltip title={ICD10_INFO_TOOLTIP} arrow>
            <InfoOutlined sx={{ fontSize: 16, color: 'text.secondary', cursor: 'help' }} />
          </Tooltip>
          <Typography variant="caption" color="text.secondary">
            ({codes.length}/{maxCodes})
          </Typography>
        </Box>
      )}

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
        {/* Selected codes as chips */}
        {codes.map((code, index) => {
          const isPrimary = index === 0;
          const description = getCodeDescription(code);
          return (
            <Tooltip
              key={code}
              title={
                <Box>
                  {description && <Typography variant="body2">{description}</Typography>}
                  {!isPrimary && (
                    <Typography variant="caption" sx={{ opacity: 0.8 }}>
                      Click to make primary
                    </Typography>
                  )}
                </Box>
              }
              arrow
            >
              <Chip
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      {code}
                    </Typography>
                    {diagnosisCodeSources?.[code] === 'ai_recommended' && (
                      <AutoAwesome
                        sx={{
                          color: isPrimary ? 'primary.contrastText' : 'secondary.main',
                          fontSize: 14,
                          opacity: isPrimary ? 0.9 : 1,
                        }}
                      />
                    )}
                  </Box>
                }
                size="small"
                variant={isPrimary ? 'filled' : 'outlined'}
                color={isPrimary ? 'primary' : 'default'}
                onDelete={disabled ? undefined : () => handleRemoveCode(code)}
                onClick={disabled || isPrimary ? undefined : () => handleMakePrimary(code)}
                sx={{
                  cursor: disabled || isPrimary ? 'default' : 'pointer',
                  '&:hover':
                    !disabled && !isPrimary
                      ? {
                          bgcolor: alpha(theme.palette.primary.main, 0.1),
                        }
                      : undefined,
                }}
              />
            </Tooltip>
          );
        })}

        {/* Autocomplete for adding codes */}
        {canAddMore && hideInputUntilActive && !isInputActive && inlineAddAction && (
          <Button
            size="small"
            variant="text"
            startIcon={<AddIcon fontSize="small" />}
            onClick={() => setInputActive(true)}
            disabled={disabled}
            sx={{ textTransform: 'none' }}
          >
            Add diagnosis
          </Button>
        )}
      </Box>

      {canAddMore && hideInputUntilActive && !isInputActive && !inlineAddAction && (
        <Button
          size="small"
          variant="text"
          startIcon={<AddIcon fontSize="small" />}
          onClick={() => setInputActive(true)}
          disabled={disabled}
          sx={{ alignSelf: 'flex-start', textTransform: 'none' }}
        >
          Add diagnosis
        </Button>
      )}

      {(canAddMore || isSingleSelect) && (!hideInputUntilActive || isInputActive) && (
        <Autocomplete<ICD10CodeOption, false, true, true>
          freeSolo
          value={undefined}
          inputValue={inputValue}
          onInputChange={(_event, newInputValue, reason) => {
            if (reason === 'input') {
              const cleaned = newInputValue
                .toUpperCase()
                .replace(/[^A-Z0-9.-]/g, '')
                .slice(0, 10);
              setInputValue(cleaned);
            } else if (reason === 'clear') {
              setInputValue('');
            }
          }}
          onChange={handleChange}
          options={ICD10_CODES}
          getOptionLabel={option => {
            if (typeof option === 'string') return option;
            return option.icd10;
          }}
          filterOptions={filterOptions}
          groupBy={groupBy}
          renderOption={renderOption}
          renderInput={params => (
            <TextField
              {...params}
              placeholder={
                codes.length === 0
                  ? 'Search for primary ICD-10 code...'
                  : isSingleSelect
                    ? 'Search to replace ICD-10 code...'
                    : 'Add another ICD-10 code...'
              }
              size={size}
              disabled={disabled}
              onKeyDown={handleKeyDown}
              autoFocus={hideInputUntilActive}
              onBlur={() => {
                if (hideInputUntilActive && !inputValue.trim()) {
                  setInputActive(false);
                }
              }}
              slotProps={{
                ...params.slotProps,
                inputLabel: { ...params.slotProps.inputLabel, shrink: true },
                htmlInput: {
                  ...params.slotProps.htmlInput,
                  maxLength: 10,
                },
              }}
            />
          )}
          slotProps={{ listbox: { sx: { maxHeight: 400 } } }}
          sx={{
            '& .MuiAutocomplete-input': {
              fontFamily: 'inherit',
            },
          }}
        />
      )}

      {/* Max codes reached message (single-select replaces in place, so no cap message) */}
      {!canAddMore && !isSingleSelect && (
        <Typography variant="caption" color="text.secondary">
          Maximum {maxCodes} codes reached
        </Typography>
      )}
    </Stack>
  );
};
